import { formatCurrency, formatDateTime } from "../../../utils/formatters";
import { TrendingUp, TrendingDown } from "lucide-react";
import Badge from "../../common/Badge";

const descriptionLabel = (desc) => {
  const map = {
    0: "Ticket Sale",
    1: "PG Fee",
    2: "Payout",
    3: "Top-Up",
    4: "Commission",
    TicketSale: "Ticket Sale",
    PaymentGatewayFee: "PG Fee",
    Payout: "Payout",
    WalletTopUp: "Top-Up",
    Commission: "Commission",
  };
  return map[desc] ?? "Transaction";
};

const descriptionVariant = (desc) => {
  const key = typeof desc === "string" ? desc : descriptionLabel(desc);
  switch (key) {
    case "Ticket Sale":
    case "TicketSale":
      return "success";
    case "Payout":
      return "info";
    case "Commission":
      return "warning";
    case "Top-Up":
    case "WalletTopUp":
      return "default";
    default:
      return "default";
  }
};

const TransactionsList = ({ transactions, loading }) => {
  if (loading)
    return (
      <div>
        {Array.from({ length: 5 }).map((_, i) => (
          <div
            key={i}
            className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 items-center px-5 py-3 border-b border-border animate-pulse"
          >
            <div className="space-y-1.5">
              <div className="h-3 bg-bg-elevated rounded w-32" />
              <div className="h-2.5 bg-bg-elevated rounded w-24" />
            </div>
            <div className="h-5 bg-bg-elevated rounded-full w-20" />
            <div className="h-3 bg-bg-elevated rounded w-20" />
            <div className="h-3 bg-bg-elevated rounded w-16 ml-auto" />
          </div>
        ))}
      </div>
    );

  if (!transactions?.length)
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <p className="text-xs text-text-muted">No transactions yet</p>
      </div>
    );

  return (
    <div>
      {/* Header */}
      <div className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 px-5 py-3 bg-bg-elevated text-xs text-text-muted uppercase tracking-wide border-b border-border">
        <span>Description</span>
        <span>Type</span>
        <span>Date</span>
        <span className="text-right">Amount</span>
      </div>

      {transactions.map((tx) => {
        const isCredit =
          tx.transactionType === 0 || tx.transactionType === "Credit";

        return (
          <div
            key={tx.id}
            className="grid grid-cols-[2fr_1fr_1fr_1fr] gap-4 items-center px-5 py-3 border-b border-border last:border-0 hover:bg-bg-elevated transition"
          >
            {/* Description */}
            <div>
              <p className="text-sm text-text-primary font-medium truncate">
                {tx.name}
              </p>
              {tx.reference && (
                <code className="text-xs text-text-hint">{tx.reference}</code>
              )}
            </div>

            {/* Type badge */}
            <div>
              <Badge
                label={descriptionLabel(tx.transactionDescription)}
                variant={descriptionVariant(tx.transactionDescription)}
              />
            </div>

            {/* Date */}
            <div className="text-xs text-text-muted">
              {formatDateTime(tx.createdDate)}
            </div>

            {/* Amount */}
            <div className="flex items-center justify-end gap-1.5">
              {isCredit ? (
                <TrendingUp size={12} className="text-primary" />
              ) : (
                <TrendingDown size={12} className="text-red-400" />
              )}
              <span
                className={`text-sm font-semibold ${
                  isCredit ? "text-primary" : "text-red-400"
                }`}
              >
                {isCredit ? "+" : "-"}
                {formatCurrency(tx.amount)}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TransactionsList;
